import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation } from "wouter";
import {
  ArrowRight, Banknote, BedDouble, Command, FileText,
  Lightbulb, Loader2, Search, Sparkles, User as UserIcon,
} from "lucide-react";

interface CommandResult {
  answer: string;
  type: string;
  items?: { label: string; value: string; href?: string }[];
  navigateTo?: string | null;
}

type SearchHit = {
  kind: "apartment" | "owner" | "cleaner" | "reservation" | "invoice";
  id: number;
  label: string;
  sublabel?: string;
  href: string;
};

type SearchResults = {
  apartments: SearchHit[];
  owners: SearchHit[];
  cleaners: SearchHit[];
  reservations: SearchHit[];
  invoices: SearchHit[];
};

const QUICK_COMMANDS = [
  "Show unpaid reservations",
  "Which apartment earned the most?",
  "How many cleaning jobs are pending?",
  "Revenue breakdown this month",
  "Who is my best cleaner?",
  "Current occupancy rate",
];

const KIND_ICONS = {
  apartment: BedDouble,
  owner: UserIcon,
  cleaner: Sparkles,
  reservation: FileText,
  invoice: Banknote,
} as const;

const KIND_LABELS = {
  apartment: "Apartments",
  owner: "Owners",
  cleaner: "Cleaners",
  reservation: "Reservations",
  invoice: "Invoices",
} as const;

function totalHits(r: SearchResults | null): number {
  if (!r) return 0;
  return r.apartments.length + r.owners.length + r.cleaners.length + r.reservations.length + r.invoices.length;
}

export default function LumiCommand() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<CommandResult | null>(null);
  const [entityResults, setEntityResults] = useState<SearchResults | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const [, setLocation] = useLocation();

  // Flatten the grouped results into a single keyboard-navigable list, in the
  // same display order as the rendered groups (apartment → reservation → owner
  // → cleaner → invoice).
  const flatHits = useMemo<SearchHit[]>(() => {
    if (!entityResults) return [];
    return [
      ...entityResults.apartments,
      ...entityResults.reservations,
      ...entityResults.owners,
      ...entityResults.cleaners,
      ...entityResults.invoices,
    ];
  }, [entityResults]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setResult(null);
      setEntityResults(null);
      setHighlightIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Reset highlight when the result list changes shape
  useEffect(() => {
    setHighlightIdx(0);
  }, [flatHits.length]);

  // Debounced entity search — fires while typing, clears the AI result
  useEffect(() => {
    if (!isOpen) return;
    const trimmed = query.trim();
    if (trimmed.length < 1) {
      setEntityResults(null);
      setIsSearching(false);
      return;
    }
    let cancelled = false;
    const timeout = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await fetch(`/api/admin/search?q=${encodeURIComponent(trimmed)}`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error("Search failed");
        const data: SearchResults = await res.json();
        if (!cancelled) setEntityResults(data);
      } catch {
        if (!cancelled) setEntityResults(null);
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [query, isOpen]);

  const executeCommand = useCallback(async (q: string) => {
    if (!q.trim() || isLoading) return;
    setIsLoading(true);
    setResult(null);

    try {
      const response = await fetch("/api/admin/lumi/command", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ query: q }),
      });

      if (!response.ok) throw new Error("Failed");
      const data = await response.json();
      setResult(data);

      if (data.navigateTo) {
        setTimeout(() => {
          setLocation(data.navigateTo);
          setIsOpen(false);
        }, 1500);
      }
    } catch {
      setResult({ answer: "Something went wrong. Try again.", type: "text" });
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, setLocation]);

  const navigateTo = useCallback((href: string) => {
    setLocation(href);
    setIsOpen(false);
  }, [setLocation]);

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" && flatHits.length > 0) {
      e.preventDefault();
      setHighlightIdx(i => (i + 1) % flatHits.length);
      return;
    }
    if (e.key === "ArrowUp" && flatHits.length > 0) {
      e.preventDefault();
      setHighlightIdx(i => (i - 1 + flatHits.length) % flatHits.length);
      return;
    }
    if (e.key === "Enter") {
      // Cmd/Ctrl+Enter forces Lumi AI even if a hit is highlighted.
      if (!e.metaKey && !e.ctrlKey && flatHits.length > 0) {
        const hit = flatHits[highlightIdx];
        if (hit) {
          e.preventDefault();
          navigateTo(hit.href);
          return;
        }
      }
      executeCommand(query);
    }
  };

  if (!isOpen) return null;

  const trimmed = query.trim();
  const hasQuery = trimmed.length > 0;
  const hits = totalHits(entityResults);

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh] sm:pt-[15vh] px-4 sm:px-0" data-testid="lumi-command-palette">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsOpen(false)} />

      <div className="relative w-full max-w-lg bg-card border rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 border-b">
          <div className="w-6 h-6 rounded-md bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
            <Lightbulb className="w-3.5 h-3.5 text-white" />
          </div>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder="Search apartments, guests, owners… or ask Lumi"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            data-testid="input-lumi-command"
          />
          {isLoading || isSearching ? (
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          ) : (
            <kbd className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded border font-mono">ESC</kbd>
          )}
        </div>

        {/* Entity search results — shown while typing, hidden once user runs Lumi AI */}
        {hasQuery && !result && !isLoading && entityResults && hits > 0 && (
          <div className="p-2 max-h-[360px] overflow-y-auto space-y-2">
            {(() => {
              let flatIdx = 0;
              return (["apartment", "reservation", "owner", "cleaner", "invoice"] as const).map(kind => {
                const list = entityResults[`${kind}s` as keyof SearchResults] as SearchHit[];
                if (!list.length) return null;
                const Icon = KIND_ICONS[kind];
                return (
                  <div key={kind}>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider px-2 pt-1 pb-0.5 font-semibold">
                      {KIND_LABELS[kind]}
                    </p>
                    {list.map(hit => {
                      const myIdx = flatIdx++;
                      const isHighlighted = myIdx === highlightIdx;
                      return (
                        <button
                          key={`${hit.kind}-${hit.id}`}
                          onClick={() => navigateTo(hit.href)}
                          onMouseEnter={() => setHighlightIdx(myIdx)}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors text-left ${
                            isHighlighted ? "bg-accent text-accent-foreground" : "hover:bg-accent/60"
                          }`}
                          data-testid={`command-hit-${hit.kind}-${hit.id}`}
                        >
                          <Icon className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
                          <span className="flex-1 min-w-0 truncate">{hit.label}</span>
                          {hit.sublabel ? (
                            <span className="text-xs text-muted-foreground truncate max-w-[40%]">{hit.sublabel}</span>
                          ) : null}
                          <ArrowRight className={`w-3 h-3 shrink-0 ${isHighlighted ? "text-foreground" : "text-muted-foreground/60"}`} />
                        </button>
                      );
                    })}
                  </div>
                );
              });
            })()}
            <div className="border-t pt-2 px-2">
              <button
                onClick={() => executeCommand(query)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-amber-600 hover:bg-amber-500/10 rounded-lg transition-colors text-left"
                data-testid="command-ask-lumi"
              >
                <Lightbulb className="w-3.5 h-3.5 shrink-0" />
                <span className="flex-1 truncate">Ask Lumi: "{trimmed}"</span>
                <kbd className="text-[10px] text-muted-foreground bg-muted px-1 py-0.5 rounded border font-mono">↵</kbd>
              </button>
            </div>
          </div>
        )}

        {/* Empty state — hint to type or run a quick command */}
        {!hasQuery && !result && !isLoading && (
          <div className="p-2 max-h-[300px] overflow-y-auto">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider px-2 py-1 font-semibold">Quick commands</p>
            {QUICK_COMMANDS.map((cmd, i) => (
              <button
                key={i}
                onClick={() => { setQuery(cmd); executeCommand(cmd); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors text-left"
                data-testid={`command-quick-${i}`}
              >
                <Search className="w-3.5 h-3.5 shrink-0 text-amber-500" />
                {cmd}
              </button>
            ))}
          </div>
        )}

        {/* No matches — typed but search returned 0 — fall through to ask Lumi */}
        {hasQuery && !result && !isLoading && entityResults && hits === 0 && !isSearching && (
          <div className="p-4 space-y-3">
            <p className="text-xs text-muted-foreground">
              No matches found. Press Enter to ask Lumi instead.
            </p>
            <button
              onClick={() => executeCommand(query)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-amber-600 hover:bg-amber-500/10 rounded-lg transition-colors text-left"
              data-testid="command-ask-lumi-fallback"
            >
              <Lightbulb className="w-3.5 h-3.5 shrink-0" />
              <span className="flex-1 truncate">Ask Lumi: "{trimmed}"</span>
              <kbd className="text-[10px] text-muted-foreground bg-muted px-1 py-0.5 rounded border font-mono">↵</kbd>
            </button>
          </div>
        )}

        {isLoading && (
          <div className="p-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            Asking Lumi…
          </div>
        )}

        {result && (
          <div className="p-4 max-h-[350px] overflow-y-auto space-y-3">
            <p className="text-sm leading-relaxed" data-testid="command-answer">{result.answer}</p>

            {result.items && result.items.length > 0 && (
              <div className="space-y-1">
                {result.items.map((item, i) => (
                  <div
                    key={i}
                    className={`flex items-center justify-between px-3 py-2 rounded-lg bg-muted/50 text-sm ${item.href ? "cursor-pointer hover:bg-accent" : ""}`}
                    onClick={() => { if (item.href) navigateTo(item.href); }}
                    data-testid={`command-item-${i}`}
                  >
                    <span className="text-muted-foreground">{item.label}</span>
                    <span className="font-medium flex items-center gap-1">
                      {item.value}
                      {item.href && <ArrowRight className="w-3 h-3" />}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {result.navigateTo && (
              <p className="text-xs text-amber-600 flex items-center gap-1">
                <ArrowRight className="w-3 h-3" />
                Navigating to {result.navigateTo}…
              </p>
            )}
          </div>
        )}

        <div className="border-t px-4 py-2 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <kbd className="bg-muted px-1 py-0.5 rounded border font-mono">
                <Command className="w-2.5 h-2.5 inline" />K
              </kbd>
              toggle
            </span>
            <span className="flex items-center gap-1">
              <kbd className="bg-muted px-1 py-0.5 rounded border font-mono">↑↓</kbd>
              navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="bg-muted px-1 py-0.5 rounded border font-mono">↵</kbd>
              {flatHits.length > 0 ? "open" : "ask Lumi"}
            </span>
            <span className="flex items-center gap-1">
              <kbd className="bg-muted px-1 py-0.5 rounded border font-mono">
                <Command className="w-2.5 h-2.5 inline" />↵
              </kbd>
              ask Lumi
            </span>
          </div>
          <span className="text-[10px] text-muted-foreground">Powered by Lumi AI</span>
        </div>
      </div>
    </div>
  );
}
